(function (global) {
  'use strict';

  const stable = (value) => JSON.stringify(value, (_, item) => item && !Array.isArray(item) && typeof item === 'object' ? Object.keys(item).sort().reduce((result, key) => { result[key] = item[key]; return result; }, {}) : item);
  const properties = (line) => line.properties || {};
  const plan = (line) => line.selling_plan_allocation || line.selling_plan || null;
  const variantId = (line) => Number(line.variant_id || line.id);
  const codes = (cart) => (cart.discount_codes || []).map((entry) => typeof entry === 'string' ? entry : entry.code).filter(Boolean);
  const bundle = (line) => Boolean(line.parent_relationship || line.parent_id || line.parent_key || line.has_components || (line.item_components || []).length || (line.components || []).length);
  const metadata = (cart) => stable({ currency: cart.currency, token: cart.token, note: cart.note || '', attributes: cart.attributes || {} });
  const lineIdentity = (line) => ({ id: variantId(line), product: Number(line.product_id), quantity: Number(line.quantity), properties: properties(line), plan: plan(line) });
  const fingerprint = (cart) => stable({ metadata: metadata(cart), items: (cart.items || []).map((line) => ({ ...lineIdentity(line), key: line.key, original: line.original_line_price, final: line.final_line_price, discounts: line.line_level_discount_allocations || [] })), codes: codes(cart), total: cart.total_price, discount: cart.total_discount, applications: cart.cart_level_discount_applications || [] });
  const hasDiscount = (cart) => codes(cart).length > 0 || Number(cart.total_discount || 0) !== 0 || (cart.cart_level_discount_applications || []).length > 0 || (cart.items || []).some((line) => Number(line.total_discount || line.line_level_total_discount || 0) !== 0 || (line.discounts || []).length > 0 || (line.line_level_discount_allocations || []).length > 0 || Number(line.final_line_price) !== Number(line.original_line_price));

  function eligible(cart, config, now = Date.now(), onSkip = () => {}) {
    const skip = (reason) => { onSkip(reason); return null; };
    if (!config.allowed || config.country !== 'US' || config.currency !== 'USD' || cart.currency !== 'USD') return skip('market');
    if (!Array.isArray(cart.items) || cart.items.length !== 1 || cart.item_count !== 1) return skip('cart-size');
    if (hasDiscount(cart)) return skip('discount');
    const line = cart.items[0];
    if (Number(line.quantity) !== 1) return skip('quantity');
    if (plan(line)) return skip('subscription');
    if (bundle(line)) return skip('bundle');
    if (line.instructions?.can_remove === false || line.instructions?.can_update_quantity === false) return skip('locked-line');
    if (Object.keys(properties(line)).length) return skip('properties');
    if (!line.key) return skip('line-key');
    const offer = (config.offers || []).find((item) => Number(item.sourceId) === variantId(line) && Number(item.productId) === Number(line.product_id));
    if (!offer) return skip('unsupported-variant');
    if (!offer.code && !config.previewMode) return skip('no-code');
    if (!offer.targetAvailable) return skip('target-unavailable');
    if (!Number.isInteger(offer.discount) || offer.discount <= 0 || !Number.isInteger(offer.sourcePrice) || !Number.isInteger(offer.targetPrice)) return skip('invalid-quote');
    if (!offerMath(offer)) return skip('invalid-quote');
    if (Number(line.original_price) !== offer.sourcePrice || Number(line.original_line_price) !== offer.sourcePrice || offer.targetPrice - offer.discount <= offer.sourcePrice) return skip('price');
    const expiresAt = config.discountsExpiresAt ? Date.parse(config.discountsExpiresAt) : null;
    const expired = expiresAt !== null && (!Number.isFinite(expiresAt) || expiresAt <= now);
    const acceptanceBlock = !offer.code || expired ? 'inactive' : !config.buyerAllowed ? 'sign_in' : null;
    return { offer, line, expiresAt, acceptanceBlock, canAccept: !acceptanceBlock, fingerprint: fingerprint(cart), metadata: metadata(cart) };
  }

  function catalogMatches(product, quote) {
    if (Number(product.id) !== Number(quote.offer.productId) || !Array.isArray(product.variants)) return false;
    const source = product.variants.find((item) => Number(item.id) === Number(quote.offer.sourceId));
    const target = product.variants.find((item) => Number(item.id) === Number(quote.offer.targetId));
    return Boolean(source && target && target.available && Number(source.price) === quote.offer.sourcePrice && Number(target.price) === quote.offer.targetPrice && !target.requires_selling_plan && !product.requires_selling_plan);
  }

  function shape(cart, quote, transaction) {
    if (metadata(cart) !== quote.metadata || !Array.isArray(cart.items)) return 'changed';
    const expectedSource = stable(lineIdentity(quote.line));
    const source = cart.items.filter((line) => stable(lineIdentity(line)) === expectedSource);
    const target = cart.items.filter((line) => variantId(line) === Number(quote.offer.targetId) && Number(line.product_id) === Number(quote.offer.productId) && Number(line.quantity) === 1 && !plan(line) && !bundle(line) && Object.keys(properties(line)).length === 0);
    if (source.length === 1 && cart.items.length === 1 && cart.item_count === 1) return 'source';
    if (target.length === 1 && cart.items.length === 1 && cart.item_count === 1) return 'target';
    return 'changed';
  }

  function appliedDiscount(cart, quote, transaction) {
    if (shape(cart, quote, transaction) !== 'target') return false;
    const line = cart.items[0];
    const allocations = line.line_level_discount_allocations || [];
    const application = allocations[0] && allocations[0].discount_application;
    const code = String(quote.offer.code).toUpperCase();
    const activeCodes = codes(cart).map((item) => String(item).toUpperCase());
    if (activeCodes.some((item) => item !== code)) return false;
    return Number(line.original_price) === quote.offer.targetPrice && Number(line.original_line_price) === quote.offer.targetPrice && Number(line.final_price) === quote.offer.targetPrice - quote.offer.discount && Number(line.final_line_price) === quote.offer.targetPrice - quote.offer.discount && Number(cart.total_discount) === quote.offer.discount && Number(cart.total_price) === quote.offer.targetPrice - quote.offer.discount && !(cart.cart_level_discount_applications || []).length && allocations.length === 1 && Number(allocations[0].amount) === quote.offer.discount && application && application.type === 'discount_code' && String(application.title).toUpperCase() === code;
  }

  function onlyConfiguredDiscount(cart, quote) {
    if (!hasDiscount(cart)) return true;
    const expected = String(quote.offer.code).toUpperCase();
    const entered = codes(cart).map((code) => String(code).toUpperCase());
    const allocations = cart.items.flatMap((line) => line.line_level_discount_allocations || []);
    return !(cart.cart_level_discount_applications || []).length && (entered.length > 0 || allocations.length > 0) && entered.every((code) => code === expected) && allocations.every((allocation) => allocation.discount_application?.type === 'discount_code' && String(allocation.discount_application.title).toUpperCase() === expected) && cart.items.every((line) => !(line.discounts || []).length || (line.discounts || []).every((discount) => String(discount.title).toUpperCase() === expected));
  }

  const CART_QUERY = `query MedjoolDaysPreviewCart($id: ID!) {
    cart(id: $id) {
      id note totalQuantity attributes { key value }
      cost { subtotalAmount { amount currencyCode } }
      lines(first: 2) {
        pageInfo { hasNextPage }
        nodes {
          __typename id quantity attributes { key value }
          merchandise { ... on ProductVariant { id product { id } } }
          cost { amountPerQuantity { amount currencyCode } subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } }
          ... on CartLine { sellingPlanAllocation { sellingPlan { id } } }
        }
      }
    }
  }`;
  const SWAP_MUTATION = `mutation MedjoolDaysPreviewSwap($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
    cartLinesUpdate(cartId: $cartId, lines: $lines) {
      cart { id }
      userErrors { code field message }
      warnings { code message }
    }
  }`;

  function cartId(cart) {
    const token = String(cart.token || '');
    if (!token || !token.includes('?key=')) return null;
    return token.startsWith('gid://shopify/Cart/') ? token : `gid://shopify/Cart/${token}`;
  }

  function attributesObject(attributes) {
    if (!Array.isArray(attributes)) return null;
    const result = {};
    for (const attribute of attributes) {
      if (!attribute || typeof attribute.key !== 'string' || typeof attribute.value !== 'string' || Object.hasOwn(result, attribute.key)) return null;
      Object.defineProperty(result, attribute.key, { value: attribute.value, enumerable: true });
    }
    return result;
  }

  function usdCents(money) {
    if (!money || money.currencyCode !== 'USD' || typeof money.amount !== 'string' || !/^\d+(?:\.\d{1,2})?$/.test(money.amount)) return null;
    const [dollars, fraction = ''] = money.amount.split('.');
    const cents = Number(dollars) * 100 + Number(fraction.padEnd(2, '0'));
    return Number.isSafeInteger(cents) ? cents : null;
  }

  function containsAjaxAttributes(storefrontAttributes, ajaxAttributes) {
    const values = attributesObject(storefrontAttributes);
    return values !== null && Object.entries(ajaxAttributes || {}).every(([key, value]) => Object.hasOwn(values, key) && values[key] === value);
  }

  function storefrontProof(cart, storefront, onSkip = () => {}) {
    const skip = (reason) => { onSkip(reason); return null; };
    const expectedId = cartId(cart);
    if (!storefront) return skip('not-found');
    if (!expectedId || storefront.id !== expectedId) return skip('identity');
    if (storefront.totalQuantity !== 1 || cart.item_count !== 1 || cart.items?.length !== 1 || storefront.lines?.pageInfo?.hasNextPage !== false || storefront.lines?.nodes?.length !== 1) return skip('line-count');
    if (String(storefront.note || '') !== String(cart.note || '') || !containsAjaxAttributes(storefront.attributes, cart.attributes)) return skip('metadata');
    if (cart.currency !== 'USD' || usdCents(storefront.cost?.subtotalAmount) !== Number(cart.total_price)) return skip('pricing');
    const line = storefront.lines.nodes[0];
    const existing = cart.items[0];
    if (line.__typename !== 'CartLine' || typeof line.id !== 'string' || !line.id.startsWith('gid://shopify/CartLine/') || line.merchandise?.id !== `gid://shopify/ProductVariant/${variantId(existing)}` || line.merchandise?.product?.id !== `gid://shopify/Product/${existing.product_id}`) return skip('line-identity');
    if (line.quantity !== existing.quantity) return skip('quantity');
    if (stable(attributesObject(line.attributes)) !== stable(properties(existing))) return skip('properties');
    if (usdCents(line.cost?.amountPerQuantity) !== Number(existing.original_price) || usdCents(line.cost?.subtotalAmount) !== Number(existing.original_line_price) || usdCents(line.cost?.totalAmount) !== Number(existing.final_line_price)) return skip('pricing');
    const storefrontPlan = line.sellingPlanAllocation?.sellingPlan?.id || null;
    const ajaxPlan = plan(existing)?.selling_plan?.id;
    if (storefrontPlan !== (ajaxPlan ? `gid://shopify/SellingPlan/${ajaxPlan}` : null)) return skip('plan');
    return { cartId: expectedId, lineId: line.id, fingerprint: fingerprint(cart) };
  }

  function bounded(task, deadline, signal) {
    return new Promise((resolve, reject) => {
      const controller = new AbortController();
      let settled = false;
      let timer;
      const finish = (callback, value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener('abort', cancel);
        callback(value);
      };
      const failure = (code) => Object.assign(new Error(code), { code });
      const cancel = () => { finish(reject, failure('MD_UPGRADE_CANCELLED')); controller.abort(); };
      if (signal?.aborted) { cancel(); return; }
      const remaining = deadline - Date.now();
      if (remaining <= 0) { finish(reject, failure('MD_UPGRADE_TIMEOUT')); return; }
      signal?.addEventListener('abort', cancel, { once: true });
      timer = setTimeout(() => { finish(reject, failure('MD_UPGRADE_TIMEOUT')); controller.abort(); }, remaining);
      Promise.resolve().then(() => {
        if (controller.signal.aborted) throw failure('MD_UPGRADE_CANCELLED');
        return task({ signal: controller.signal });
      }).then((value) => finish(resolve, value), (error) => finish(reject, error));
    });
  }

  function offerMath(offer) {
    if (!Number.isSafeInteger(offer.sourceWeight) || !Number.isSafeInteger(offer.targetWeight) || offer.sourceWeight <= 0 || offer.targetWeight <= 0) return null;
    const added = offer.targetWeight - offer.sourceWeight;
    const increase = offer.targetPrice - offer.discount - offer.sourcePrice;
    if (added <= 0 || !Number.isSafeInteger(increase) || increase <= 0) return null;
    return { added, increase, unit: increase / added };
  }

  async function replace(quote, api, options = {}) {
    let writesStarted = false;
    const expired = () => quote.expiresAt !== null && (!Number.isFinite(quote.expiresAt) || quote.expiresAt <= Date.now());
    if (!quote.canAccept || !quote.offer.code || expired() || typeof api.preflight !== 'function' || typeof api.swap !== 'function') return { status: 'blocked', cart: null, writesStarted };
    const forwardDeadline = Date.now() + (options.forwardTimeoutMs ?? 12000);
    const call = (task, deadline = forwardDeadline) => bounded(task, deadline, options.signal);
    const read = (deadline) => call((context) => api.read(context), deadline);
    const state = (cart) => shape(cart, quote);
    const stage = (value) => { if (!options.signal?.aborted) options.onStage?.(value); };
    const terminal = (error) => ['MD_UPGRADE_TIMEOUT', 'MD_UPGRADE_CANCELLED'].includes(error?.code);
    const swapAndRead = async (payload, deadline) => {
      writesStarted = true;
      try { await call((context) => api.swap(payload, context), deadline); } catch (error) { if (terminal(error)) throw error; }
      return read(deadline);
    };
    const discountCart = async (discount, deadline) => {
      writesStarted = true;
      try {
        const result = await call((context) => api.discount({ discount }, context), deadline);
        if (Array.isArray(result?.items) && typeof result.token === 'string') return result;
      } catch (error) { if (terminal(error)) throw error; }
      return read(deadline);
    };
    let proof;
    try {
      stage('checking');
      const [original, product] = await Promise.all([read(), call((context) => api.product(quote.offer.handle, context))]);
      if (fingerprint(original) !== quote.fingerprint) return { status: 'changed', cart: original, writesStarted };
      if (!catalogMatches(product, quote)) return { status: 'unavailable', cart: original, writesStarted };
      proof = storefrontProof(original, await call((context) => api.preflight(original, context)));
      if (!proof) return { status: 'unavailable', cart: original, writesStarted };
      if (expired()) return { status: 'blocked', cart: original, writesStarted };
    } catch (_) { return { status: 'unavailable', cart: null, writesStarted }; }
    let current;
    try {
      stage('upgrading');
      current = await swapAndRead({ cartId: proof.cartId, lines: [{ id: proof.lineId, merchandiseId: `gid://shopify/ProductVariant/${quote.offer.targetId}` }] });
      if (state(current) === 'source' && !hasDiscount(current)) return { status: 'restored', cart: current, writesStarted };
      if (state(current) !== 'target' || hasDiscount(current)) throw new Error('swap');
      stage('applying');
      current = await discountCart(quote.offer.code);
      if (!appliedDiscount(current, quote)) throw new Error('discount');
      return { status: 'accepted', cart: current, writesStarted };
    } catch (error) {
      if (options.signal?.aborted) return { status: 'review', cart: null, writesStarted };
      const recoveryDeadline = Date.now() + (options.recoveryTimeoutMs ?? 6000);
      stage('recovering');
      try {
        current = await read(recoveryDeadline);
        if (state(current) === 'target' && appliedDiscount(current, quote)) return { status: 'accepted', cart: current, writesStarted };
        if (terminal(error)) return { status: 'review', cart: current, writesStarted };
        if (state(current) === 'source' && !hasDiscount(current)) return { status: 'restored', cart: current, writesStarted };
        if (state(current) !== 'target' || !onlyConfiguredDiscount(current, quote)) return { status: 'review', cart: current, writesStarted };
        if (hasDiscount(current)) {
          current = await discountCart('', recoveryDeadline);
          if (state(current) !== 'target' || hasDiscount(current)) return { status: 'review', cart: current, writesStarted };
        }
        const rollbackProof = storefrontProof(current, await call((context) => api.preflight(current, context), recoveryDeadline));
        if (!rollbackProof) return { status: 'review', cart: current, writesStarted };
        current = await swapAndRead({ cartId: rollbackProof.cartId, lines: [{ id: rollbackProof.lineId, merchandiseId: `gid://shopify/ProductVariant/${quote.offer.sourceId}` }] }, recoveryDeadline);
        return { status: state(current) === 'source' && !hasDiscount(current) ? 'restored' : 'review', cart: current, writesStarted };
      } catch (_) { return { status: 'review', cart: null, writesStarted }; }
    }
  }

  const EXPERIMENT_ATTRIBUTES = ['md_box_experiment', 'md_box_assignment', 'md_box_arm'];
  const DELIVERY_VERSION = '2026-10-28-v2';
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const VISITOR_KEY = 'md_box_upgrade_visitor_v1';
  const SESSION_KEY = 'md_box_upgrade_session_v1';
  const CART_SCOPE_KEY = 'md_box_upgrade_cart_v2';

  function analyticsAllowed(shopify) {
    try { return shopify?.customerPrivacy?.analyticsProcessingAllowed() === true; } catch (_) { return false; }
  }

  async function loadPrivacy(shopify, deadline) {
    if (typeof shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') return;
    if (typeof shopify?.loadFeatures !== 'function') return;
    try {
      await bounded(() => new Promise((resolve) => {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => resolve());
      }), deadline);
    } catch (_) { /* Checkout continues when Shopify cannot provide the consent API. */ }
  }

  function experimentIdentity(environment) {
    if (!analyticsAllowed(environment.Shopify)) return null;
    try {
      // These are our own analytics IDs. Shopify's cookies are never inspected or changed.
      const visitorStore = environment.localStorage || environment.sessionStorage;
      const sessionStore = environment.sessionStorage;
      if (!visitorStore || !sessionStore || typeof environment.crypto?.randomUUID !== 'function') return null;
      let visitorId = visitorStore.getItem(VISITOR_KEY);
      let sessionId = sessionStore.getItem(SESSION_KEY);
      if (!UUID.test(visitorId || '')) { visitorId = environment.crypto.randomUUID(); visitorStore.setItem(VISITOR_KEY, visitorId); }
      if (!UUID.test(sessionId || '')) { sessionId = environment.crypto.randomUUID(); sessionStore.setItem(SESSION_KEY, sessionId); }
      if (visitorStore.getItem(VISITOR_KEY) !== visitorId || sessionStore.getItem(SESSION_KEY) !== sessionId) return null;
      return { visitorId, sessionId };
    } catch (_) { return null; }
  }

  function checkoutScope(environment) {
    try {
      const value = JSON.parse(environment.sessionStorage?.getItem(CART_SCOPE_KEY) || 'null');
      if (!value || !/^[a-f0-9]{64}$/.test(value.cartHash || '') || !UUID.test(value.sessionId || '') || typeof value.experimentId !== 'string') return null;
      for (const assignment of [value.assignmentId ? value : null, value.cleanupOwner]) {
        if (assignment && (!UUID.test(assignment.assignmentId || '') || !['control', 'treatment'].includes(assignment.arm))) return null;
      }
      if ([value, value.cleanupOwner].filter(Boolean).some((owner) => ['tagWritePending'].some((key) => owner[key] !== undefined && typeof owner[key] !== 'boolean'))) return null;
      if (['enrolled', 'readyPending', 'empty'].some((key) => value[key] !== undefined && typeof value[key] !== 'boolean') || value.readyEventId !== undefined && !UUID.test(value.readyEventId)) return null;
      return value;
    } catch (_) { return null; }
  }

  function saveCheckoutScope(environment, scope) {
    try {
      environment.sessionStorage.setItem(CART_SCOPE_KEY, JSON.stringify(scope));
      return environment.sessionStorage.getItem(CART_SCOPE_KEY) === JSON.stringify(scope);
    } catch (_) { return false; }
  }

  async function checkoutCartHash(environment, cart) {
    if (typeof cart.token !== 'string' || !cart.token || typeof environment.crypto?.subtle?.digest !== 'function') return null;
    const bytes = await environment.crypto.subtle.digest('SHA-256', new TextEncoder().encode(cart.token));
    return Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  async function checkoutIdentity(environment, experiment, cart, signal) {
    if (!analyticsAllowed(environment.Shopify)) return null;
    const previous = checkoutScope(environment);
    if (EXPERIMENT_ATTRIBUTES.some((key) => cart.attributes?.[key]) && (!previous || previous.experimentId !== experiment.id || !ownsAssignment(cart, experiment.id, previous.cleanupOwner || previous))) return { legacy: true };
    const cartHash = await checkoutCartHash(environment, cart);
    if (!cartHash || signal?.aborted || !analyticsAllowed(environment.Shopify)) return null;
    const identity = experimentIdentity(environment);
    if (!identity) return null;
    const changed = Boolean(previous && (previous.cartHash !== cartHash || previous.experimentId !== experiment.id || previous.empty || previous.sessionId !== identity.sessionId));
    let scope = previous;
    if (!scope || changed || scope.sessionId !== identity.sessionId) {
      identity.sessionId = environment.crypto.randomUUID();
      try { environment.sessionStorage.setItem(SESSION_KEY, identity.sessionId); }
      catch (_) { return null; }
      scope = { experimentId: experiment.id, cartHash, sessionId: identity.sessionId, empty: false };
      const owner = previous?.cleanupOwner || previous;
      if (owner && (ownsAssignment(cart, experiment.id, owner) || owner.tagWritePending)) scope.cleanupOwner = { assignmentId: owner.assignmentId, arm: owner.arm, ...(owner.tagWritePending ? { tagWritePending: true } : {}) };
    }
    if (!saveCheckoutScope(environment, scope)) return null;
    return { identity, scope, previous, changed };
  }

  function ownsAssignment(cart, experimentId, assignment) {
    return cart.attributes?.md_box_experiment === experimentId && cart.attributes?.md_box_assignment === assignment?.assignmentId && cart.attributes?.md_box_arm === assignment?.arm;
  }

  function assignmentAttributes(cart, experiment, assignment) {
    if (assignment?.experimentId !== experiment.id || !UUID.test(assignment?.assignmentId || '') || !['control', 'treatment'].includes(assignment?.arm)) return null;
    const values = { md_box_experiment: experiment.id, md_box_assignment: assignment.assignmentId, md_box_arm: assignment.arm };
    const existing = cart.attributes || {};
    if (EXPERIMENT_ATTRIBUTES.some((key) => existing[key] !== undefined && existing[key] !== '' && existing[key] !== values[key])) return null;
    return { ...existing, ...values };
  }

  const core = { eligible, catalogMatches, fingerprint, shape, appliedDiscount, replace, hasDiscount, cartId, storefrontProof, usdCents, offerMath, bounded, analyticsAllowed, loadPrivacy, experimentIdentity, checkoutIdentity, checkoutScope, assignmentAttributes, CART_QUERY, SWAP_MUTATION };
  if (typeof module === 'object' && module.exports) module.exports = core;
  if (!global.document || global.__mdCartUpgrade) return;

  class CartUpgrade {
    constructor(element) {
      this.element = element;
      this.config = JSON.parse(element.querySelector('[data-md-upgrade-config]').textContent);
      this.dialog = element.querySelector('dialog');
      this.copy = this.config.copy;
      this.phase = 'idle';
      this.bypass = false;
      this.root = this.config.root || '/';
      this.api = {
        read: (context) => this.request('cart.js', undefined, context),
        product: (handle, context) => this.request(`products/${encodeURIComponent(handle)}.js`, undefined, context),
        preflight: (cart, context) => this.storefront(CART_QUERY, { id: cartId(cart) }, context).then((data) => data.cart),
        swap: (variables, context) => this.storefront(SWAP_MUTATION, variables, context).then((data) => data.cartLinesUpdate),
        discount: (body, context) => this.updateCart(body, context),
        tag: (attributes, context) => this.updateCart({ attributes }, context)
      };
      global.addEventListener('submit', (event) => this.onSubmit(event), true);
      global.addEventListener('click', (event) => this.onClick(event), true);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); if (this.phase === 'offer') this.decline(); });
      element.querySelector('[data-md-upgrade-accept]').addEventListener('click', () => this.accept());
      element.querySelector('[data-md-upgrade-decline]').addEventListener('click', () => this.decline());
      element.querySelector('[data-md-upgrade-close]').addEventListener('click', () => { if (this.phase === 'offer') this.decline(); });
      this.operationEpoch = 0;
      if (this.config.experiment?.enabled === true) {
        this.privacyReady = loadPrivacy(global.Shopify, Date.now() + 750);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, ({ cartData } = {}) => {
          if (cartData?.item_count !== 0 || !Array.isArray(cartData.items) || !analyticsAllowed(global.Shopify)) return;
          const saved = checkoutScope(global);
          if (saved?.experimentId === this.config.experiment.id) { saved.empty = true; saveCheckoutScope(global, saved); this.assignment = null; }
        });
        document.addEventListener('visitorConsentCollected', () => {
          if (analyticsAllowed(global.Shopify)) return;
          this.assignment = null;
          this.assignmentScope = null;
          try { global.localStorage?.removeItem(VISITOR_KEY); global.sessionStorage?.removeItem(VISITOR_KEY); global.sessionStorage?.removeItem(SESSION_KEY); global.sessionStorage?.removeItem(CART_SCOPE_KEY); } catch (_) { /* Storage can be unavailable. */ }
          if (this.phase === 'loading') { this.operationEpoch += 1; this.operationController?.abort(); this.continueNative(); }
          else if (this.phase === 'offer') this.continueNative();
        });
      }
      global.addEventListener('pagehide', () => { this.operationEpoch += 1; this.operationController?.abort(); });
      global.addEventListener('pageshow', (event) => { if (event.persisted) { this.operationEpoch += 1; this.operationController?.abort(); this.phase = 'idle'; this.bypass = false; if (this.dialog.open) this.dialog.close(); } });
      element.dataset.mdUpgradeReady = 'true';
      element.dataset.mdUpgradeState = 'ready';
    }

    async request(path, body, context = {}) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (context.signal?.aborted) controller.abort();
      else context.signal?.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(() => controller.abort(), 8000);
      try {
        const response = await fetch(this.root + path, { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal });
        if (!response.ok) throw new Error('request');
        return await response.json();
      } catch (error) {
        if (controller.signal.aborted) throw Object.assign(new Error('request aborted'), { code: context.signal?.aborted ? 'MD_UPGRADE_CANCELLED' : 'MD_UPGRADE_TIMEOUT' });
        throw error;
      } finally { clearTimeout(timeout); context.signal?.removeEventListener('abort', abort); }
    }

    async updateCart(body, context = {}) {
      const request = (path, value) => context.httpTimeoutMs ? bounded((step) => this.request(path, value, step), Date.now() + context.httpTimeoutMs, context.signal) : this.request(path, value, context);
      if (context.signal?.aborted) throw Object.assign(new Error('cart update cancelled'), { code: 'MD_UPGRADE_CANCELLED' });
      context.onStage?.('cart-tag');
      await request('cart/update.js', body);
      // Shopify can expose private attributes only in the mutation response.
      // Read the same canonical cart shape used by every strict comparison.
      if (context.signal?.aborted) throw Object.assign(new Error('cart update cancelled'), { code: 'MD_UPGRADE_CANCELLED' });
      context.onStage?.('cart-readback');
      return request('cart.js', undefined);
    }

    async storefront(query, variables, context) {
      if (!variables.id && !variables.cartId) throw new Error('cart identity');
      const result = await this.request('api/2026-07/graphql.json', { query, variables }, context);
      if (result.errors?.length || !result.data) throw new Error('storefront');
      const mutation = result.data.cartLinesUpdate;
      if (mutation && (mutation.userErrors?.length || mutation.warnings?.length)) throw new Error('storefront update');
      return result.data;
    }

    cartForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      const path = new URL(form.action, location.href).pathname.replace(/\/$/, '');
      return path === (this.root + 'cart').replace(/\/$/, '');
    }

    formMatches(cart) {
      if (!this.intent.form) return true;
      const data = new FormData(this.intent.form);
      if (data.has('note') && String(data.get('note') || '') !== String(cart.note || '')) { this.element.dataset.mdUpgradeReason = 'note'; return false; }
      const drawerNote = this.intent.form.closest('cart-drawer')?.querySelector('textarea[name="note"]');
      if (drawerNote && String(drawerNote.value || '') !== String(cart.note || '')) { this.element.dataset.mdUpgradeReason = 'drawer-note'; return false; }
      for (const [name, value] of data.entries()) {
        const match = /^attributes\[(.+)\]$/.exec(name);
        if (match && String(value) !== String((cart.attributes || {})[match[1]] || '')) { this.element.dataset.mdUpgradeReason = 'attributes'; return false; }
      }
      const quantities = data.getAll('updates[]');
      const matches = !quantities.length || quantities.length === cart.items.length && quantities.every((quantity, index) => Number(quantity) === Number(cart.items[index].quantity));
      if (!matches) this.element.dataset.mdUpgradeReason = 'quantities';
      return matches;
    }

    onSubmit(event) {
      if (!this.config.allowed || !this.cartForm(event.target)) return;
      const submitter = event.submitter || (document.activeElement?.form === event.target && document.activeElement?.name === 'checkout' ? document.activeElement : null);
      if (!submitter || submitter.name !== 'checkout') return;
      if (this.bypass) { this.bypass = false; return; }
      event.preventDefault();
      event.stopImmediatePropagation();
      this.element.dataset.mdUpgradeIntent = 'checkout-submit';
      this.start({ form: event.target, submitter });
    }

    onClick(event) {
      const submitter = event.target instanceof Element ? event.target.closest('button[name="checkout"], input[name="checkout"]') : null;
      if (submitter && submitter.type === 'submit' && this.cartForm(submitter.form)) {
        this.element.dataset.mdUpgradeIntent = 'checkout-click';
        if (!this.config.allowed) { this.element.dataset.mdUpgradeState = 'skip-disabled'; return; }
        if (submitter.disabled) { this.element.dataset.mdUpgradeState = 'skip-disabled-button'; return; }
        if (event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) { this.element.dataset.mdUpgradeState = 'skip-button'; return; }
        if (event.defaultPrevented) this.element.dataset.mdUpgradeReason = 'previously-prevented';
        if (this.bypass) { this.bypass = false; return; }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.start({ form: submitter.form, submitter });
        return;
      }
      if (!this.config.allowed || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || link.target || link.hasAttribute('download') || !link.closest('cart-drawer, cart-items, #main-cart-footer, .cart__ctas, .cart-drawer')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.search || url.hash || url.pathname !== this.root + 'checkout') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.element.dataset.mdUpgradeIntent = 'checkout-link';
      this.start({ link });
    }

    trackingEndpoint(path) {
      const endpoint = new URL(this.config.experiment.endpoint);
      if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || endpoint.search || endpoint.hash) throw new Error('tracking endpoint');
      return endpoint.href.replace(/\/$/, '') + path;
    }

    async trackingRequest(path, body, context = {}, keepalive = false) {
      if (!analyticsAllowed(global.Shopify)) throw new Error('analytics unavailable');
      const response = await fetch(this.trackingEndpoint(path), {
        method: 'POST', mode: 'cors', credentials: 'omit', keepalive,
        headers: { 'Content-Type': 'text/plain;charset=UTF-8', Accept: 'application/json' },
        body: JSON.stringify(body), signal: context.signal
      });
      if (!response.ok) throw new Error('tracking unavailable');
      return response.json();
    }

    errorDiagnostics() {
      const stages = ['configuration', 'privacy', 'identity', 'assignment', 'assignment-response', 'assignment-attributes', 'cart-tag', 'cart-readback', 'cart-recheck', 'preflight', 'ready', 'post-ready', 'initial-cart', 'catalog', 'enrollment', 'render', 'rebind', 'cleanup', 'unknown'];
      const reasons = ['timeout', 'cancelled', 'request-error', 'unavailable', 'invalid-response', 'attributes', 'consent', 'ineligible', 'fingerprint', 'form', 'not-recorded', 'proof-not-found', 'proof-identity', 'proof-line-count', 'proof-metadata', 'proof-pricing', 'proof-line-identity', 'proof-quantity', 'proof-properties', 'proof-plan', 'unknown'];
      const cleanups = ['not-needed', 'cleared', 'uncertain', 'preserved-ready', 'unknown'];
      const elapsed = Date.now() - this.admissionStartedAt;
      const elapsedBucket = !Number.isFinite(elapsed) || elapsed < 0 ? 'unknown' : elapsed < 250 ? 'lt250' : elapsed < 500 ? '250-499' : elapsed < 1000 ? '500-999' : elapsed < 1500 ? '1000-1499' : elapsed < 2000 ? '1500-1999' : 'gte2000';
      const stage = this.element.dataset.mdUpgradeReadinessStage;
      const reason = this.element.dataset.mdUpgradeReadinessReason;
      const cleanup = this.cleanupStatus || 'not-needed';
      return { stage: stages.includes(stage) ? stage : 'unknown', reason: reasons.includes(reason) ? reason : 'unknown', elapsedBucket, cleanup: cleanups.includes(cleanup) ? cleanup : 'unknown' };
    }

    async event(event, reason, deadline = Date.now() + 750, signal) {
      const assignment = this.assignment;
      if (!assignment || !analyticsAllowed(global.Shopify) || signal?.aborted || (!['ready', 'error'].includes(event) && assignment.enrolled !== true)) return false;
      if (event === 'ready' && assignment.enrolled === true) return true;
      let body;
      try { body = { assignmentId: assignment.assignmentId, receipt: assignment.receipt, eventId: event === 'ready' && UUID.test(this.assignmentScope?.readyEventId || '') ? this.assignmentScope.readyEventId : global.crypto.randomUUID(), event, deliveryVersion: DELIVERY_VERSION, ...(reason ? { reason } : {}), ...(event === 'error' ? { diagnostics: this.errorDiagnostics() } : {}) }; }
      catch (_) { return false; }
      const until = Math.min(deadline, Date.now() + 750);
      if (event !== 'ready') {
        try {
          const result = await bounded((context) => this.trackingRequest('/v1/events', body, context, true), until, signal);
          return result?.recorded === true;
        } catch (_) { return false; }
      }
      // Both requests share one event ID and deadline. A hedge must not abort a
      // healthy first response merely because half the available time elapsed.
      return new Promise((resolve) => {
        let finished = false;
        let started = 0;
        let failed = 0;
        let hedgeTimer;
        let deadlineTimer;
        const controllers = [];
        const finish = (value) => {
          if (finished) return;
          finished = true;
          clearTimeout(hedgeTimer);
          clearTimeout(deadlineTimer);
          signal?.removeEventListener('abort', cancel);
          controllers.forEach((controller) => controller.abort());
          resolve(value);
        };
        const cancel = () => finish(false);
        const launch = () => {
          if (finished || started >= 2 || Date.now() >= until || signal?.aborted || !analyticsAllowed(global.Shopify)) return;
          started += 1;
          const controller = new AbortController();
          controllers.push(controller);
          bounded((context) => {
            if (!analyticsAllowed(global.Shopify) || !this.assignmentScope || this.assignmentScope.assignmentId !== assignment.assignmentId) throw new Error('ready scope unavailable');
            const pending = { ...this.assignmentScope, readyPending: true, readyEventId: body.eventId };
            if (!saveCheckoutScope(global, pending)) throw new Error('ready scope unavailable');
            Object.assign(this.assignmentScope, pending);
            assignment.readyDispatched = true;
            return this.trackingRequest('/v1/events', body, context);
          }, until, controller.signal).then((result) => {
            if (finished || signal?.aborted) return;
            if (result?.recorded === true && result?.enrolled === true) { assignment.enrolled = true; finish(true); return; }
            retry();
          }, () => { if (!finished) retry(); });
        };
        const retry = () => {
          failed += 1;
          if (started < 2) { clearTimeout(hedgeTimer); launch(); }
          if (failed >= 2) finish(false);
        };
        if (signal?.aborted || Date.now() >= until) { finish(false); return; }
        signal?.addEventListener('abort', cancel, { once: true });
        deadlineTimer = setTimeout(() => finish(false), until - Date.now());
        hedgeTimer = setTimeout(launch, Math.max(1, Math.floor((until - Date.now()) / 2)));
        launch();
      });
    }

    assignmentOwner(assignment) {
      const scope = this.assignmentScope || checkoutScope(global);
      const owner = [scope, scope?.cleanupOwner].find((item) => item?.assignmentId === assignment?.assignmentId && item?.arm === assignment?.arm);
      return owner ? { scope, owner } : null;
    }

    saveOwnerState(assignment, changes) {
      if (!analyticsAllowed(global.Shopify)) return false;
      const found = this.assignmentOwner(assignment);
      if (!found) return false;
      Object.assign(found.owner, changes);
      return saveCheckoutScope(global, found.scope);
    }

    uncertainCleanup() {
      this.cleanupStatus = 'uncertain';
      this.element.dataset.mdUpgradeCleanup = 'unconfirmed';
    }

    async clearAssignmentTags(cart, assignment, deadline, signal) {
      const experimentId = this.config.experiment?.id;
      const owner = this.assignmentOwner(assignment)?.owner || assignment;
      const uncertain = Boolean(owner?.tagWritePending);
      if (signal?.aborted) { if (uncertain) this.uncertainCleanup(); return null; }
      if (!ownsAssignment(cart, experimentId, assignment) && !uncertain) return cart;
      const call = (task) => bounded(task, deadline, signal);
      try {
        const fresh = await call((context) => this.api.read(context));
        // A clean read does not prove an aborted POST cannot commit later.
        if (!ownsAssignment(fresh, experimentId, assignment)) {
          if (uncertain) { this.uncertainCleanup(); return null; }
          return fresh;
        }
        if (fresh.token !== cart.token && fingerprint(fresh) !== fingerprint({ ...cart, token: fresh.token })) { this.uncertainCleanup(); return null; }
        // Positive ownership establishes that the sole tag POST committed.
        if (!this.saveOwnerState(assignment, { tagWritePending: false })) { this.uncertainCleanup(); return null; }
        const cleared = await call((context) => this.api.tag(Object.fromEntries(EXPERIMENT_ATTRIBUTES.map((key) => [key, ''])), context));
        if (cleared?.token === fresh.token && Array.isArray(cleared.items) && !EXPERIMENT_ATTRIBUTES.some((key) => cleared.attributes?.[key])) {
          if (!this.saveOwnerState(assignment, { tagWritePending: false })) { this.uncertainCleanup(); return null; }
          this.cleanupStatus = 'cleared';
          this.element.dataset.mdUpgradeCleanup = 'cleared';
          return cleared;
        }
      } catch (_) {}
      this.uncertainCleanup();
      return null;
    }

    async cleanupBoundCart(cart, deadline, signal) {
      if (!analyticsAllowed(global.Shopify)) return cart;
      const saved = checkoutScope(global);
      if (!saved || saved.experimentId !== this.config.experiment?.id) return cart;
      const binding = await bounded((context) => checkoutIdentity(global, this.config.experiment, cart, context.signal), deadline, signal);
      if (!binding || binding.legacy) return cart;
      if (cart.item_count === 0) { binding.scope.empty = true; saveCheckoutScope(global, binding.scope); }
      if (!binding.changed && (saved.enrolled === true || saved.readyPending === true) && cart.item_count !== 0) return cart;
      const cleared = await this.clearAssignmentTags(cart, saved.cleanupOwner || saved, deadline, signal);
      if (cleared && !EXPERIMENT_ATTRIBUTES.some((key) => cleared.attributes?.[key])) { delete binding.scope.cleanupOwner; saveCheckoutScope(global, binding.scope); }
      return cleared || cart;
    }

    readinessFailure(stage, reason) {
      const stages = ['configuration', 'privacy', 'identity', 'assignment', 'assignment-response', 'assignment-attributes', 'cart-tag', 'cart-readback', 'cart-recheck', 'preflight', 'ready', 'post-ready', 'initial-cart', 'catalog', 'enrollment', 'render', 'rebind', 'cleanup', 'unknown'];
      const reasons = ['timeout', 'cancelled', 'request-error', 'unavailable', 'invalid-response', 'attributes', 'consent', 'ineligible', 'fingerprint', 'form', 'not-recorded', 'proof-not-found', 'proof-identity', 'proof-line-count', 'proof-metadata', 'proof-pricing', 'proof-line-identity', 'proof-quantity', 'proof-properties', 'proof-plan', 'unknown'];
      const safeStage = stages.includes(stage) ? stage : 'unknown';
      const safeReason = reasons.includes(reason) ? reason : 'unknown';
      this.element.dataset.mdUpgradeReadinessStage = safeStage;
      this.element.dataset.mdUpgradeReadinessReason = safeReason;
      try { global.console?.debug?.('[Medjool Days box upgrade] readiness failed', this.errorDiagnostics()); } catch (_) {}
    }

    readinessError(error) {
      return error?.code === 'MD_UPGRADE_TIMEOUT' ? 'timeout' : error?.code === 'MD_UPGRADE_CANCELLED' || error?.name === 'AbortError' ? 'cancelled' : 'request-error';
    }

    async enrollExperiment(cart, quote, deadline, signal, catalogPromise) {
      const experiment = this.config.experiment;
      const fail = (stage, reason) => { this.readinessFailure(stage, reason); return null; };
      const observe = async (stage, task) => {
        this.readinessStageActive = stage;
        try { return await task(); }
        catch (error) { if (!signal?.aborted || !this.element.dataset.mdUpgradeReadinessStage) this.readinessFailure(stage, this.readinessError(error)); throw error; }
      };
      if (!experiment?.enabled || !experiment.id || !experiment.endpoint || !quote.canAccept) return fail('configuration', 'unavailable');
      const workDeadline = deadline - 200;
      await observe('privacy', () => bounded(() => this.privacyReady || loadPrivacy(global.Shopify, workDeadline), workDeadline, signal));
      const binding = await observe('identity', () => bounded((context) => checkoutIdentity(global, experiment, cart, context.signal), workDeadline, signal));
      if (!binding || binding.legacy) return fail('identity', 'unavailable');
      const call = (task) => bounded(task, workDeadline, signal);
      if (binding.previous && (binding.changed || binding.previous.tagWritePending || binding.previous.enrolled !== true && binding.previous.readyPending !== true)) {
        cart = await this.clearAssignmentTags(cart, binding.previous.cleanupOwner || binding.previous, workDeadline, signal);
        quote = cart && eligible(cart, this.config);
        if (!quote?.canAccept) return fail('assignment-attributes', 'attributes');
        if (!EXPERIMENT_ATTRIBUTES.some((key) => cart.attributes?.[key])) { delete binding.scope.cleanupOwner; saveCheckoutScope(global, binding.scope); }
      }
      const identity = binding.identity;
      if (!analyticsAllowed(global.Shopify)) return fail('preflight', 'consent');
      let proofReason = 'unavailable';
      const [catalog, storefront] = await Promise.all([
        catalogPromise || call((context) => this.api.product(quote.offer.handle, context)).then((product) => ({ product }), (error) => ({ error })),
        observe('preflight', () => call((context) => this.api.preflight(cart, context)))
      ]);
      if (catalog.error) { this.readinessFailure('catalog', this.readinessError(catalog.error)); return null; }
      if (!catalogMatches(catalog.product, quote)) return fail('catalog', 'unavailable');
      const proof = storefrontProof(cart, storefront, (reason) => { proofReason = reason; this.element.dataset.mdStorefrontReason = reason; });
      if (!proof) return fail('preflight', `proof-${proofReason}`);
      const request = {
        experimentId: experiment.id, ...identity, deliveryVersion: DELIVERY_VERSION,
        sourceVariantId: String(quote.offer.sourceId), targetVariantId: String(quote.offer.targetId),
        currency: 'USD', sourceCents: quote.offer.sourcePrice, targetCents: quote.offer.targetPrice, discountCents: quote.offer.discount
      };
      const assignment = await observe('assignment', () => call((context) => this.trackingRequest('/v1/assign', request, context)));
      if (typeof assignment?.receipt !== 'string' || assignment.receipt.length < 16 || !Number.isFinite(Date.parse(assignment.receiptExpiresAt)) || Date.parse(assignment.receiptExpiresAt) <= Date.now() || typeof assignment.enrolled !== 'boolean' || binding.changed && assignment.assignmentId === binding.previous?.assignmentId) return fail('assignment-response', 'invalid-response');
      const attributes = assignmentAttributes(cart, experiment, assignment);
      if (!attributes || !analyticsAllowed(global.Shopify)) return fail('assignment-attributes', !attributes ? 'attributes' : 'consent');
      this.assignment = assignment;
      this.assignmentScope = binding.scope;
      Object.assign(binding.scope, { assignmentId: assignment.assignmentId, arm: assignment.arm, enrolled: assignment.enrolled });
      if (!saveCheckoutScope(global, binding.scope)) { this.assignment = null; return fail('identity', 'unavailable'); }
      let completed = false;
      let failureEvent;
      let tagged = cart;
      let cleanupCart = { ...cart, attributes };
      try {
        const tags = Object.fromEntries(EXPERIMENT_ATTRIBUTES.map((key) => [key, attributes[key]]));
        let tagStage = 'cart-tag';
        try {
          tagged = ownsAssignment(cart, experiment.id, assignment) ? cart : await call((context) => {
            if (!this.saveOwnerState(assignment, { tagWritePending: true })) throw new Error('tag scope unavailable');
            return this.api.tag(tags, { ...context, onStage: (stage) => { tagStage = stage; this.readinessStageActive = stage; } });
          });
        } catch (error) { if (!signal?.aborted || !this.element.dataset.mdUpgradeReadinessStage) this.readinessFailure(tagStage, this.readinessError(error)); throw error; }
        if (!Array.isArray(tagged?.items) || typeof tagged.token !== 'string') tagged = await observe('cart-readback', () => call((context) => this.api.read(context)));
        if (ownsAssignment(tagged, experiment.id, assignment)) cleanupCart = tagged;
        const expected = { ...cart, token: tagged.token, attributes };
        const freshQuote = eligible(tagged, this.config);
        const mismatch = !freshQuote?.canAccept ? 'ineligible' : fingerprint(tagged) !== fingerprint(expected) ? 'fingerprint' : !this.formMatches(tagged) ? 'form' : null;
        if (mismatch) { failureEvent = 'cart_changed'; return fail('cart-recheck', mismatch); }
        if (!this.saveOwnerState(assignment, { tagWritePending: false })) return fail('identity', 'unavailable');
        if (tagged.token !== cart.token) {
          const reboundProof = storefrontProof(tagged, await observe('rebind', () => call((context) => this.api.preflight(tagged, context))), (reason) => { proofReason = reason; this.element.dataset.mdStorefrontReason = reason; });
          if (!reboundProof) return fail('rebind', `proof-${proofReason}`);
          binding.scope.cartHash = await bounded(() => checkoutCartHash(global, tagged), workDeadline, signal);
          if (!binding.scope.cartHash || !saveCheckoutScope(global, binding.scope)) return fail('identity', 'unavailable');
        }
        if (!analyticsAllowed(global.Shopify) || signal?.aborted) return fail('preflight', signal?.aborted ? 'cancelled' : 'consent');
        let ready = await this.event('ready', undefined, workDeadline, signal);
        if (!ready && Date.now() < workDeadline && !signal?.aborted && analyticsAllowed(global.Shopify)) {
          try {
            const confirmation = await call((context) => this.trackingRequest('/v1/assign', request, context));
            if (confirmation?.assignmentId === assignment.assignmentId && confirmation?.experimentId === assignment.experimentId && confirmation?.arm === assignment.arm && confirmation?.enrolled === true) { assignment.enrolled = true; ready = true; }
          } catch (_) {}
        }
        if (!ready) { failureEvent = 'unavailable'; return fail('ready', 'not-recorded'); }
        binding.scope.enrolled = true;
        delete binding.scope.readyPending;
        delete binding.scope.readyEventId;
        saveCheckoutScope(global, binding.scope);
        if (!analyticsAllowed(global.Shopify) || signal?.aborted) return fail('post-ready', signal?.aborted ? 'cancelled' : 'consent');
        completed = true;
        return { quote: freshQuote, arm: assignment.arm };
      } catch (_) { failureEvent = 'cart_tagging_failed'; return null; }
      finally {
        if (!completed) {
          if (!binding.scope.readyPending && assignment.enrolled !== true && !assignment.readyDispatched) await this.clearAssignmentTags(cleanupCart, assignment, deadline, signal);
          else this.cleanupStatus = 'preserved-ready';
          if (failureEvent) void this.event('error', failureEvent);
          this.assignment = null;
          this.assignmentScope = null;
        }
      }
    }

    async start(intent) {
      if (this.phase !== 'idle') return;
      this.phase = 'loading';
      this.element.dataset.mdUpgradeState = 'loading';
      delete this.element.dataset.mdUpgradeReason;
      delete this.element.dataset.mdUpgradeReadinessStage;
      delete this.element.dataset.mdUpgradeReadinessReason;
      delete this.element.dataset.mdUpgradeCleanup;
      this.admissionStartedAt = Date.now();
      this.readinessStageActive = 'initial-cart';
      this.cleanupStatus = 'not-needed';
      this.intent = intent;
      this.assignment = null;
      this.assignmentScope = null;
      this.forceDirectCheckout = false;
      this.restoreFocus = document.activeElement;
      const controller = new AbortController();
      this.operationController = controller;
      const epoch = ++this.operationEpoch;
      let finished = false;
      let watchdog;
      const active = () => !finished && epoch === this.operationEpoch && !controller.signal.aborted;
      const continueActive = () => {
        if (!active()) return;
        finished = true;
        clearTimeout(watchdog);
        this.operationEpoch += 1;
        controller.abort();
        this.continueNative();
      };
      const deadline = Date.now() + 2000;
      const workDeadline = deadline - 200;
      const call = (task) => bounded(task, workDeadline, controller.signal);
      let readinessStage = 'initial-cart';
      watchdog = setTimeout(() => {
        if (!active()) return;
        if (!this.element.dataset.mdUpgradeReadinessStage) this.readinessFailure(this.readinessStageActive || readinessStage, 'timeout');
        const owner = this.assignmentOwner(this.assignment)?.owner;
        if (owner?.tagWritePending) this.uncertainCleanup();
        this.element.dataset.mdUpgradeState = 'tracking-unavailable';
        continueActive();
      }, Math.max(0, deadline - Date.now()));
      if (intent.submitter) intent.submitter.setAttribute('aria-busy', 'true');
      try {
        let cart = await call((context) => this.api.read(context));
        if (!active()) return;
        if (!this.formMatches(cart)) { this.element.dataset.mdUpgradeState = 'form-mismatch'; return continueActive(); }
        let quote = eligible(cart, this.config, undefined, (reason) => { this.element.dataset.mdUpgradeReason = reason; });
        if (!quote) {
          if (this.config.experiment?.enabled === true) await this.cleanupBoundCart(cart, deadline, controller.signal);
          if (!active()) return;
          this.element.dataset.mdUpgradeState = 'ineligible'; return continueActive();
        }
        // Begin this read immediately, but never let it reject unobserved while
        // privacy/identity/owned-metadata cleanup prepares the same cart.
        const catalogPromise = call((context) => this.api.product(quote.offer.handle, context)).then((product) => ({ product }), (error) => ({ error }));
        if (this.config.experiment?.enabled === true) {
          readinessStage = 'enrollment';
          const enrollment = await this.enrollExperiment(cart, quote, deadline, controller.signal, catalogPromise);
          if (!active()) return;
          if (!enrollment) { this.element.dataset.mdUpgradeState = 'tracking-unavailable'; return continueActive(); }
          quote = enrollment.quote;
          if (enrollment.arm === 'control') { this.element.dataset.mdUpgradeState = 'control'; return continueActive(); }
        } else {
          if (!this.config.previewMode) { this.element.dataset.mdUpgradeState = 'experiment-disabled'; return continueActive(); }
          readinessStage = 'catalog';
          const [catalog, storefront] = await Promise.all([
            catalogPromise,
            call((context) => this.api.preflight(cart, context)).then((value) => ({ value }), (error) => ({ error }))
          ]);
          if (!active()) return;
          if (catalog.error) throw catalog.error;
          if (!catalogMatches(catalog.product, quote)) { this.element.dataset.mdUpgradeState = 'catalog-mismatch'; return continueActive(); }
          const proof = !storefront.error && storefrontProof(cart, storefront.value, (reason) => { this.element.dataset.mdStorefrontReason = reason; });
          if (storefront.error) this.element.dataset.mdStorefrontReason = storefront.error.message === 'storefront' ? 'graphql-error' : 'request-error';
          this.element.dataset.mdStorefrontState = proof ? 'matched' : 'unavailable';
          if (!proof && quote.canAccept) { quote.canAccept = false; quote.acceptanceBlock = 'unavailable'; }
        }
        if (!active() || Date.now() >= workDeadline) return continueActive();
        readinessStage = 'render';
        this.quote = quote;
        this.render(quote);
        this.phase = 'offer';
        this.element.dataset.mdUpgradeState = 'offer';
        this.dialog.showModal();
        finished = true;
        clearTimeout(watchdog);
        void this.event('shown');
      } catch (error) {
        if (!this.element.dataset.mdUpgradeReadinessStage) this.readinessFailure(readinessStage, this.readinessError(error));
        if (active()) { this.element.dataset.mdUpgradeState = 'exception'; continueActive(); }
      }
      finally { clearTimeout(watchdog); controller.abort(); if (intent.submitter) intent.submitter.removeAttribute('aria-busy'); }
    }

    text(selector, value) { this.element.querySelector(selector).textContent = value; }
    fill(template, replacements) { return Object.entries(replacements).reduce((text, [key, value]) => text.split(`__${key}__`).join(value), template); }
    money(cents) { return new Intl.NumberFormat(this.config.locale || 'en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: Number.isInteger(cents) && cents % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 }).format(cents / 100); }

    render(quote) {
      const offer = quote.offer;
      const math = offerMath(offer);
      if (!math) throw new Error('invalid offer weights');
      this.text('[data-md-upgrade-title]', this.fill(this.copy.title, { ADDED: math.added, AMOUNT: this.money(math.increase) }));
      this.text('[data-md-upgrade-product]', quote.line.product_title || offer.title);
      this.text('[data-md-upgrade-badge]', this.fill(this.copy.badge, { AMOUNT: this.money(offer.discount) }));
      this.text('[data-md-upgrade-route]', this.fill(this.copy.route, { FROM: offer.sourceLabel, TO: offer.targetLabel }));
      this.text('[data-md-upgrade-increase]', this.fill(this.copy.increase, { UNIT: this.money(math.unit) }));
      this.text('[data-md-upgrade-original]', this.money(offer.targetPrice));
      this.text('[data-md-upgrade-price]', this.money(offer.targetPrice - offer.discount));
      this.text('[data-md-upgrade-accept]', quote.canAccept ? this.fill(this.copy.accept, { ADDED: math.added }) : this.copy.preview_accept);
      this.text('[data-md-upgrade-decline]', this.copy.decline);
      const image = this.element.querySelector('[data-md-upgrade-image]');
      const raw = offer.image || quote.line.featured_image?.url || quote.line.image;
      let imageUrl;
      try { imageUrl = new URL(raw, location.href); } catch (_) {}
      image.hidden = !imageUrl || !['https:', 'http:'].includes(imageUrl.protocol);
      if (!image.hidden) { image.src = imageUrl.href; image.alt = quote.line.product_title || offer.title; }
      this.element.querySelector('[data-md-upgrade-message]').hidden = quote.canAccept;
      if (!quote.canAccept) this.text('[data-md-upgrade-message]', quote.acceptanceBlock === 'sign_in' ? this.copy.sign_in : quote.acceptanceBlock === 'unavailable' ? this.copy.unavailable : this.copy.preview);
      this.element.querySelector('[data-md-upgrade-sign-in]').hidden = quote.acceptanceBlock !== 'sign_in';
      this.element.querySelector('[data-md-upgrade-review]').hidden = true;
      this.element.querySelector('[data-md-upgrade-close]').disabled = false;
      this.element.querySelector('[data-md-upgrade-accept]').disabled = !quote.canAccept;
      this.element.querySelector('[data-md-upgrade-decline]').disabled = false;
      this.element.querySelector('[data-md-upgrade-accept]').hidden = false;
      this.element.querySelector('[data-md-upgrade-decline]').hidden = false;
    }

    continueNative() {
      const intent = this.intent;
      this.phase = 'idle';
      if (this.dialog.open) this.dialog.close();
      if (this.forceDirectCheckout) {
        location.assign(this.root + 'checkout');
      } else if (intent?.form?.isConnected && intent.submitter?.isConnected) {
        this.bypass = true;
        intent.form.requestSubmit(intent.submitter);
        this.bypass = false;
      } else if (intent?.link?.isConnected) {
        location.assign(intent.link.href);
      } else { location.assign(this.root + 'checkout'); }
    }

    decline() {
      if (this.phase !== 'offer') return;
      void this.event('decline');
      this.continueNative();
    }

    async accept() {
      if (this.phase !== 'offer' || !this.quote.canAccept) return;
      if (this.config.experiment?.enabled === true && (!analyticsAllowed(global.Shopify) || this.assignment?.enrolled !== true)) return this.continueNative();
      void this.event('accept_click');
      if (this.quote.expiresAt !== null && (!Number.isFinite(this.quote.expiresAt) || this.quote.expiresAt <= Date.now())) {
        this.quote.canAccept = false;
        this.quote.acceptanceBlock = 'inactive';
        this.render(this.quote);
        return;
      }
      this.phase = 'mutating';
      const epoch = ++this.operationEpoch;
      const controller = new AbortController();
      this.operationController = controller;
      const acceptButton = this.element.querySelector('[data-md-upgrade-accept]');
      acceptButton.setAttribute('aria-busy', 'true');
      this.element.querySelectorAll('[data-md-upgrade-accept], [data-md-upgrade-decline], [data-md-upgrade-close]').forEach((button) => { button.disabled = true; });
      this.text('[data-md-upgrade-accept]', this.copy.updating);
      let result;
      try { result = await replace(this.quote, this.api, { signal: controller.signal, onStage: (stage) => { this.element.dataset.mdUpgradeState = stage; this.text('[data-md-upgrade-accept]', this.copy[stage] || this.copy.updating); } }); }
      catch (_) { result = { status: 'changed', writesStarted: false }; }
      if (epoch !== this.operationEpoch || controller.signal.aborted) return;
      controller.abort();
      acceptButton.removeAttribute('aria-busy');
      if (result.status === 'accepted') {
        void this.event('accepted_verified');
        this.element.dataset.mdUpgradeState = 'checkout';
        this.text('[data-md-upgrade-accept]', this.copy.opening_checkout || this.copy.updating);
        location.assign(this.root + 'checkout');
        return;
      }
      void this.event('error', result.status === 'restored' ? 'restored' : result.writesStarted ? 'review_required' : 'mutation_failed');
      this.phase = result.writesStarted ? 'review' : 'offer';
      this.element.dataset.mdUpgradeState = 'review';
      this.forceDirectCheckout = !result.writesStarted;
      this.text('[data-md-upgrade-message]', result.status === 'restored' ? this.copy.restored : result.writesStarted ? this.copy.review : this.copy.changed);
      this.element.querySelector('[data-md-upgrade-message]').hidden = false;
      this.element.querySelector('[data-md-upgrade-review]').hidden = false;
      this.element.querySelector('[data-md-upgrade-sign-in]').hidden = true;
      this.element.querySelector('[data-md-upgrade-accept]').hidden = true;
      this.element.querySelector('[data-md-upgrade-decline]').hidden = Boolean(result.writesStarted);
      this.element.querySelector('[data-md-upgrade-decline]').disabled = Boolean(result.writesStarted);
      this.element.querySelector('[data-md-upgrade-close]').disabled = Boolean(result.writesStarted);
    }
  }

  const element = document.querySelector('md-cart-upgrade');
  if (element && typeof HTMLDialogElement === 'function') global.__mdCartUpgrade = new CartUpgrade(element);
})(typeof window === 'undefined' ? globalThis : window);
